import React from "react";
import styled from "styled-components";
import AniLink from "gatsby-plugin-transition-link/AniLink";

import Button, { SectionActionButton } from "../../components/form/Button";
import { colors } from "../../../theme";
import { getProjectCardColorsBySlug } from "../../styles/project-hue";
import {
  MAIN_FEATURED_PROJECT,
  SUB_FEATURED_PROJECTS,
  DISPLAYED_PROJECTS,
} from "../../utils/constants/projects";
import { getTechnologyHrefForBadge } from "../../utils/constants/technologies";
import {
  CardsStack,
  SubCards,
  MoreProjectsWrap,
  ProjectCardRoot,
  CardOverlayLink,
  CardFigure,
  CardBody,
  TitleStack,
  ClientName,
  ProjectTitle,
  BadgeRow,
  TechBadge,
  CircularCta,
  CaseStudyButtonWrap,
} from "./styles";

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StyledCaseStudyButton = styled(Button)`
  display: inline-flex;
  box-sizing: border-box;
  text-align: center;
`;

export const ProjectCard = ({ data, variant = "default", titleAs = "h3" }) => {
  const themeColors = getProjectCardColorsBySlug(data.slug);
  const { solid } = themeColors.buttons;
  const href = `/projects/${data.slug}/`;
  const heroSrc = data.media?.hero;
  const isMain = variant === "main";
  const isCompact = variant === "compact";
  const clientName = data.client?.name || data.name;
  const technologies = (data.techStack || []).slice(0, 3);
  const showBadges = !isCompact && technologies.length > 0;
  // Compact cards stay neutral until hover, then use the default card palette.
  const cardBg = isCompact ? colors.white : themeColors.card;
  const cardHoverBg = isCompact ? themeColors.card : themeColors.cardHover;
  const clientAccent = isCompact ? colors.grey.darkMedium : themeColors.accent;
  const usesHoverAccent = isCompact;
  const ctaBg = themeColors.light;

  return (
    <ProjectCardRoot
      $variant={variant}
      $bgColor={cardBg}
      $hoverBgColor={cardHoverBg}
      $accent={isCompact ? themeColors.accent : undefined}
    >
      <CardOverlayLink
        as={AniLink}
        cover
        bg={solid.hoverBackground}
        to={href}
        aria-label={`Open ${clientName} project`}
        $hideOnDesktop={isMain}
      />
      <CardFigure $variant={variant}>
        {heroSrc ? (
          <img
            src={heroSrc}
            alt={data.heading}
            width={1536}
            height={1024}
            loading="lazy"
            decoding="async"
          />
        ) : null}
      </CardFigure>
      <CardBody $variant={variant}>
        <TitleStack $variant={variant}>
          <ClientName
            $variant={variant}
            $accent={clientAccent}
            $hoverAccent={usesHoverAccent}
          >
            {clientName}
          </ClientName>
          <ProjectTitle as={titleAs} $variant={variant}>
            {data.heading}
          </ProjectTitle>
        </TitleStack>
        {showBadges ? (
          <BadgeRow>
            {technologies.map((tech) => {
              const techHref = getTechnologyHrefForBadge(tech);
              if (techHref) {
                return (
                  <TechBadge
                    key={tech}
                    as={AniLink}
                    cover
                    bg={colors.black}
                    to={techHref}
                    $border={themeColors.light}
                    $color={themeColors.accent}
                    $interactive
                  >
                    {tech}
                  </TechBadge>
                );
              }

              return (
                <TechBadge
                  key={tech}
                  $border={themeColors.light}
                  $color={themeColors.accent}
                >
                  {tech}
                </TechBadge>
              );
            })}
          </BadgeRow>
        ) : null}
        <CircularCta
          as={AniLink}
          cover
          bg={solid.hoverBackground}
          to={href}
          $bg={ctaBg}
          $compact={isCompact}
          aria-label={`Open ${clientName} project`}
        >
          <ArrowIcon />
        </CircularCta>
        {isMain ? (
          <CaseStudyButtonWrap>
            <StyledCaseStudyButton
              forwardedAs={AniLink}
              cover
              bg={solid.hoverBackground}
              to={href}
              bgColor={solid.background}
              textColor={solid.color}
              hoverBgColor={solid.hoverBackground}
              hoverTextColor={solid.hoverColor}
              hoverBorderColor={solid.hoverBorder}
            >
              Read case study
            </StyledCaseStudyButton>
          </CaseStudyButtonWrap>
        ) : null}
      </CardBody>
    </ProjectCardRoot>
  );
};

const ProjectCards = ({ mode = "landing" }) => {
  const isLanding = mode !== "all";

  if (!isLanding) {
    return (
      <CardsStack $stacked>
        {DISPLAYED_PROJECTS.map((project) => (
          <ProjectCard key={project.slug} data={project} variant="main" />
        ))}
      </CardsStack>
    );
  }

  return (
    <CardsStack>
      <ProjectCard data={MAIN_FEATURED_PROJECT} variant="main" />
      <SubCards>
        {SUB_FEATURED_PROJECTS.map((project) => (
          <ProjectCard key={project.slug} data={project} variant="default" />
        ))}
      </SubCards>
      <MoreProjectsWrap>
        <SectionActionButton
          forwardedAs={AniLink}
          cover
          bg={colors.black}
          to="/projects/"
        >
          More Projects
        </SectionActionButton>
      </MoreProjectsWrap>
    </CardsStack>
  );
};

export default ProjectCards;
